import { activeCurrency, currencySymbol } from './currency'

/**
 * Montants dans la devise de l'utilisateur (XOF par défaut). Toujours des entiers, sans centimes.
 * Un montant d'opération est strictement positif ; un solde peut être négatif.
 */

export type Xof = number

/** Plafond d'un montant saisi : 10 milliards, très au-delà d'un budget personnel. */
export const MAX_AMOUNT: Xof = 10_000_000_000

export function isValidAmount(value: unknown): value is Xof {
  return (
    typeof value === 'number' && Number.isSafeInteger(value) && value > 0 && value <= MAX_AMOUNT
  )
}

export function sum(values: readonly Xof[]): Xof {
  return values.reduce((total, value) => total + value, 0)
}

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })

const NARROW_NO_BREAK_SPACE = /\u202F/g
const NO_BREAK_SPACE = '\u00A0'
const MINUS_SIGN = '\u2212'

/**
 * `75 000 FCFA`, `75 000 €`, avec des espaces insécables (U+00A0) : Geist ne contient pas l'espace
 * fine insécable (U+202F) produite par Intl en français. Le signe moins est le vrai signe U+2212.
 * Devise : celle du profil, sauf si `currency` est précisé.
 */
export function formatMoney(
  amount: Xof,
  options: { withCurrency?: boolean; currency?: string } = {},
): string {
  const { withCurrency = true } = options
  const digits = NUMBER_FORMAT.format(Math.abs(amount)).replace(
    NARROW_NO_BREAK_SPACE,
    NO_BREAK_SPACE,
  )
  const signed = amount < 0 ? `${MINUS_SIGN}${digits}` : digits
  if (!withCurrency) return signed
  return `${signed}${NO_BREAK_SPACE}${currencySymbol(options.currency ?? activeCurrency())}`
}

/**
 * Lit une saisie utilisateur (« 75 000 », « 75000 », « 75.000 ») et renvoie un montant valide,
 * ou `null` si la saisie n'est pas un entier positif dans les limites.
 */
export function parseAmountInput(input: string): Xof | null {
  const compact = input.replace(/[\s\u00A0\u202F.]/g, '')
  if (!/^\d+$/.test(compact)) return null
  const value = Number(compact)
  return isValidAmount(value) ? value : null
}
